/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import type { ShopperStores } from '@/scapi';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import StoreAddress from './address';
import { cn } from '@/lib/utils';

/** Store shape plus the custom attributes bkcb-004 actually returns on Shopper Stores. */
export type LocatorStore = ShopperStores.schemas['Store'] & {
    c_inStoreShopping?: boolean;
    c_shopByAppointment?: boolean;
    c_customerServiceEmail?: string;
};

interface StoreCardProps {
    store: LocatorStore;
    /** Distance unit the search was run with ("mi" | "km"). */
    distanceUnit?: string;
    /** True when this store is the shopper's currently selected store. */
    isSelected: boolean;
    /** Called when the shopper presses the primary button. */
    onSelect: () => void;
}

/**
 * Weekday keys as they appear in the imported `store-hours` block, in the order
 * `Date.getDay()` returns.
 */
const DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Pull today's line out of a multi-line `store-hours` value.
 *
 * The imported blocks are one `Day: HH - HH` line per day (see
 * `.reference/data-authored-stores/sites/*\/stores.xml`). Live NM shows only the
 * current day's line in the panel, rendered `Sun. 11AM - 6PM`, so the colon is
 * normalised to a full stop to match.
 *
 * Returns null when the value does not carry a line for today -- the caller then
 * renders no hours line at all rather than guessing one.
 */
function todaysHours(storeHours?: string, now: Date = new Date()): string | null {
    if (!storeHours) return null;
    const key = DAY_KEYS[now.getDay()];
    const line = storeHours
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l.toLowerCase().startsWith(`${key.toLowerCase()}:`));
    return line ? line.replace(/^([A-Za-z]{3}):/, '$1.') : null;
}

/**
 * Strip the brand prefix from a store name.
 *
 * Live NM labels its result `Denver`, not `Neiman Marcus - Denver`: inside a
 * single-brand panel the brand word is redundant on every row. Our imported names
 * carry it (`Neiman Marcus - Dallas - Downtown`), so it is removed for display only.
 * The underlying record is untouched.
 */
function shortStoreName(name?: string): string {
    if (!name) return '';
    return name.replace(/^(Neiman Marcus|Bergdorf Goodman)\s*-\s*/i, '').trim() || name;
}

/**
 * StoreCard
 *
 * One store result, in live NM's shape: name and distance on one bold line, today's
 * hours, a checkmark list of services, a `Store Details` expander, and a solid
 * primary button.
 *
 * SERVICES ARE DATA-DRIVEN AND FAIL CLOSED. Each row renders only when the store
 * record's own boolean custom attribute is true (`c_inStoreShopping`,
 * `c_shopByAppointment`, authored in `.reference/data-authored-store-inventory` and
 * confirmed present on all 35 records via Shopper Stores). A store missing the
 * attribute shows no row -- there is no default-on fallback.
 *
 * @returns ReactElement
 */
export default function StoreCard({ store, distanceUnit = 'mi', isSelected, onSelect }: StoreCardProps): ReactElement {
    const { t } = useTranslation('extStoreLocator');
    const hours = todaysHours(store.storeHours);
    const services = [
        store.c_shopByAppointment ? t('storeLocator.details.services.shopByAppointment') : null,
        store.c_inStoreShopping ? t('storeLocator.details.services.inStoreShopping') : null,
    ].filter(Boolean) as string[];

    return (
        <div data-testid="store-card" className="py-5">
            <div className="text-base font-bold" data-testid="store-card-name">
                {shortStoreName(store.name)}
                {typeof store.distance === 'number' && (
                    <>
                        {' - '}
                        {t('storeLocator.details.distanceShort', {
                            // Live renders a whole number ("Denver - (3 mi)"). Rounding a
                            // sub-mile distance to "0 mi" reads as missing data rather than
                            // as "very close", so anything under one unit renders "<1".
                            distance: store.distance < 1 ? '<1' : Math.round(store.distance),
                            unit: distanceUnit,
                        })}
                    </>
                )}
            </div>

            {hours && (
                <div className="mt-1 text-sm text-muted-foreground" data-testid="store-card-hours">
                    {hours}
                </div>
            )}

            {services.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="store-card-services">
                    {services.map((label) => (
                        <li key={label} className="flex items-center gap-2 text-sm">
                            <Check className="size-4 shrink-0" aria-hidden="true" />
                            {label}
                        </li>
                    ))}
                </ul>
            )}

            <Accordion type="single" collapsible className="mt-2">
                <AccordionItem value="details" className="border-none">
                    <AccordionTrigger
                        data-testid="store-card-details-trigger"
                        className="justify-start gap-1 py-1 px-0 text-sm hover:no-underline [&>svg:last-child]:hidden">
                        {t('storeLocator.details.storeDetailsTitle')}
                        <ChevronDown className="size-4 shrink-0 transition-transform duration-200" aria-hidden="true" />
                    </AccordionTrigger>
                    <AccordionContent className="px-0 pb-2 text-sm text-muted-foreground">
                        <StoreAddress store={store} />
                        {store.phone && (
                            <div className="mt-2">
                                <a href={`tel:${store.phone}`} className="underline-offset-2 hover:underline">
                                    {store.phone}
                                </a>
                            </div>
                        )}
                        {store.storeHours && <div className="mt-2 whitespace-pre-line">{store.storeHours}</div>}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {/*
                A store with no inventory-list link cannot fulfil pickup, and the
                template disabled its selection control for exactly that reason. The
                control changed shape (radio -> live's solid button); the guard is
                kept rather than retired. On bkcb-004 all 35 store records now carry
                an `inventoryId`, so this never fires today -- it is the fail-closed
                branch for a store record that has not been given a list.
            */}
            <button
                type="button"
                onClick={onSelect}
                disabled={!store.inventoryId}
                aria-pressed={isSelected}
                data-testid="store-card-select"
                className={cn(
                    'mt-2 w-full max-w-[320px] px-6 py-3 text-sm tracking-wide uppercase transition-opacity',
                    'disabled:cursor-not-allowed disabled:opacity-40',
                    isSelected
                        ? 'border border-foreground bg-background text-foreground'
                        : 'bg-foreground text-background hover:opacity-80'
                )}>
                {isSelected ? t('storeLocator.details.yourStore') : t('storeLocator.details.setAsYourStore')}
            </button>
        </div>
    );
}
